import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

type Variant = 'primary' | 'ghost' | 'subtle' | 'danger';
type Size = 'sm' | 'md';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
}

const sizeClasses: Record<Size, string> = {
  sm: 'px-2.5 py-1 text-[12px] rounded-md',
  md: 'px-3 py-1.5 text-[12.5px] rounded-md',
};

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-text text-panel border border-text hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'bg-panel text-text border border-border-strong hover:bg-bg-elevated disabled:opacity-50 disabled:cursor-not-allowed',
  subtle:
    'bg-bg-elevated text-text-2 border border-transparent hover:text-text disabled:opacity-50 disabled:cursor-not-allowed',
  danger:
    'bg-danger text-white border border-danger hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'ghost', size = 'md', loading, disabled, iconLeft, iconRight, children, className = '', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 font-medium font-sans cursor-pointer transition-colors ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      {...rest}
    >
      {loading ? <Spinner /> : iconLeft}
      {children}
      {iconRight}
    </button>
  );
});

function Spinner() {
  return (
    <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
  );
}
